import React from 'react';
import Data from '../../data';
import UI from '../../ui';
import AddCarrierHeader from '../AddCarrierHeader.component';
import AddFedExDirectAccount from './AddFedExDirectAccount.component';
import { AddFedExWalletAccount } from './AddFedExWalletAccount.component';

const { Carrier } = Data;
const { Section, SectionBody, Footer, WrappedRadioButton, InlineMessage } = UI;

enum FedExAccountOption {
	RESELLER = 'reseller',
	BYOA = 'byoa',
}

type FedExSelectOptionProps = {
	readonly completionCallback: (
		success: boolean,
		carrier?: string,
		locationsLength?: number,
		carrierType?: string
	) => void;
	readonly custId?: string | null;
	readonly enterpriseLocations?: any[] | null;
	readonly isEditing?: boolean;
	readonly backAction?: () => void;
	readonly hasUserOwnedAccount?: boolean;
	readonly showSuccessModal?: boolean;
};

export function FedExSelectOption({
	completionCallback,
	custId = null,
	enterpriseLocations,
	isEditing = false,
	backAction = null,
	hasUserOwnedAccount,
	showSuccessModal = false,
}: FedExSelectOptionProps) {
	const [accountOption, setAccountOption] = React.useState(
		hasUserOwnedAccount === undefined || hasUserOwnedAccount === true
			? FedExAccountOption.RESELLER
			: FedExAccountOption.BYOA
	);
	const [currentStep, setCurrentStep] =
		React.useState<null | FedExAccountOption>(null);

	const onContinue = (): void => {
		if (accountOption === FedExAccountOption.RESELLER) {
			setCurrentStep(FedExAccountOption.RESELLER);
		} else if (accountOption === FedExAccountOption.BYOA) {
			setCurrentStep(FedExAccountOption.BYOA);
		}
	};

	if (currentStep === FedExAccountOption.RESELLER) {
		return (
			<AddFedExWalletAccount
				completionCallback={completionCallback}
				custId={custId}
				backAction={() => setCurrentStep(null)}
				showSuccessModal={showSuccessModal}
			/>
		);
	}

	if (currentStep === FedExAccountOption.BYOA) {
		return (
			<AddFedExDirectAccount
				completionCallback={completionCallback}
				custId={custId}
				enterpriseLocations={enterpriseLocations}
				backAction={() => setCurrentStep(null)}
			/>
		);
	}

	return (
		<Section>
			<AddCarrierHeader
				title="Select Account Option"
				carrier={Carrier.FedEx}
				subtitle="What type of account do you want to connect?"
			/>
			<SectionBody classNames="select-account-option-section">
				{isEditing && (
					<InlineMessage
						type="information"
						text="Switching your FedEx connection will disconnect you from your current connection."
						dataqa="fedex-select-account-editing-message"
						className="fedex-select-option-account-message"
					/>
				)}
				<WrappedRadioButton
					inputId="fedex-reseller-option"
					name="accountOption"
					value={FedExAccountOption.RESELLER}
					checked={accountOption === FedExAccountOption.RESELLER}
					onChange={e => setAccountOption(e.value as FedExAccountOption)}
					dataqa="fedex-reseller-radio-option"
					disabled={hasUserOwnedAccount === false}
				>
					I want to use Stamps.com's discounted FedEx rates.
				</WrappedRadioButton>
				<WrappedRadioButton
					inputId="fedex-byoa-option"
					name="accountOption"
					value={FedExAccountOption.BYOA}
					checked={accountOption === FedExAccountOption.BYOA}
					onChange={e => setAccountOption(e.value as FedExAccountOption)}
					dataqa="fedex-byoa-radio-option"
					disabled={hasUserOwnedAccount === true}
				>
					I want to connect my existing FedEx accounts and use my FedEx account
					rates.
				</WrappedRadioButton>
			</SectionBody>
			<Footer
				primary={{
					label: 'Continue',
					onClick: onContinue,
					dataqa: 'fedex-select-account-continue-btn',
					disabled: !accountOption,
				}}
				secondary={{
					label: 'Cancel',
					onClick: () => completionCallback(false),
					dataqa: 'fedex-select-account-cancel-btn',
				}}
				backAction={backAction ?? (() => completionCallback(false))}
			/>
		</Section>
	);
}
